import { formatTime, imageMetaConverter } from '@lib/server';
import type { APIImageType, Default } from '@types';
import type { BarOrPub, DayOfWeek, LocalBusiness, NightClub, Restaurant, WithContext } from 'schema-dts';

interface SchemaProps {
  data: Default;
  logo: APIImageType | APIImageType[];
  brandName: string;
  name: string;
}

export function PlaceSchema({ data, logo, name, brandName }: SchemaProps) {
  // Helper function to convert day string to schema.org DayOfWeek
  const getDayOfWeek = (day: string | undefined): DayOfWeek => {
    const dayMap: Record<string, DayOfWeek> = {
      Monday: 'https://schema.org/Monday',
      Tuesday: 'https://schema.org/Tuesday',
      Wednesday: 'https://schema.org/Wednesday',
      Thursday: 'https://schema.org/Thursday',
      Friday: 'https://schema.org/Friday',
      Saturday: 'https://schema.org/Saturday',
      Sunday: 'https://schema.org/Sunday',
    };
    return dayMap[day || ''] || 'https://schema.org/Monday';
  };

  const commonSchema: WithContext<LocalBusiness> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    amenityFeature: data?.facilities?.map((f) => ({
      '@type': 'LocationFeatureSpecification',
      name: f.label,
      value: f.value,
    })),
    priceRange: '£ - ££',
    location: {
      '@type': 'Place',
      name: data?.address?.cityOrTown,
    },
    logo: imageMetaConverter(logo)?.src,
    description: data?.summary,
    image: imageMetaConverter(data?.images?.primaryImage)?.src,
    parentOrganization: {
      '@type': 'Organization',
      name: brandName,
      logo: imageMetaConverter(logo)?.src,
    },
    brand: {
      '@type': 'Brand',
      name: brandName,
      logo: imageMetaConverter(logo)?.src,
    },
    subOrganization: {
      '@type': 'Organization',
      name,
      logo: imageMetaConverter(logo)?.src,
    },
    address: {
      '@type': 'PostalAddress' as const,
      streetAddress: [data?.address?.addressLine1, data?.address?.addressLine2].filter(Boolean).join(' '),
      addressLocality: data?.address?.cityOrTown,
      addressRegion: data?.address?.county,
      postalCode: data?.address?.postcode,
      addressCountry: data?.address?.country,
    },
    geo: {
      '@type': 'GeoCoordinates' as const,
      latitude: data?.geoLocation?.latitude,
      longitude: data?.geoLocation?.longitude,
    },
    telephone: data?.contact?.phoneNumber,
    email: data?.contact?.emailAddress,
    sameAs: [
      data?.social?.facebook,
      data?.social?.twitter,
      data?.social?.instagram,
      data?.social?.youTube,
      data?.social?.tikTok,
      data?.social?.googlePlace,
      data?.social?.tripadvisor,
    ].filter(Boolean),
    hasMap: data?.social?.googlePlace,
    openingHoursSpecification: data?.times?.openingTimes?.timetable
      ?.map((day) => ({
        '@type': 'OpeningHoursSpecification' as const,
        dayOfWeek: getDayOfWeek(day.dayOfWeek),
        opens: formatTime(day.openingTimes?.[0]?.openingHour || undefined, 'HH:mm a') || undefined,
        closes: formatTime(day.openingTimes?.[0]?.closingHour || undefined, 'HH:mm a') || undefined,
      }))
      .filter((hours) => hours.opens && hours.closes),
    specialOpeningHoursSpecification: data?.times?.openingTimes?.special
      ?.map((special) => ({
        '@type': 'OpeningHoursSpecification' as const,
        validFrom: formatTime(special.openingTimes?.[0]?.date, 'd MMM yyyy') || undefined,
        validThrough: formatTime(special.openingTimes?.[0]?.date, 'd MMM yyyy') || undefined,
        opens: formatTime(special.openingTimes?.[0]?.openingHour || undefined, 'HH:mm a') || undefined,
        closes: formatTime(special.openingTimes?.[0]?.closingHour || undefined, 'HH:mm a') || undefined,
      }))
      .filter((hours) => hours.opens && hours.closes),
  };

  const schemaByType = {
    localBusiness: {
      ...commonSchema,
      '@type': 'LocalBusiness' as const,
    },
    restaurant: {
      ...commonSchema,
      '@type': 'Restaurant' as const,
      hasMenu: 'yes',
      acceptsReservations: 'yes',
      servesCuisine: data.facilities?.find((f) => f.label.includes('food'))?.value,
    } as WithContext<Restaurant>,
    barOrPub: {
      ...commonSchema,
      '@type': 'BarOrPub' as const,
      hasMenu: 'yes',
      acceptsReservations: 'yes',
      servesCuisine: data.facilities?.find((f) => f.label.includes('food'))?.value,
    } as WithContext<BarOrPub>,
    nightclub: {
      ...commonSchema,
      '@type': 'NightClub' as const,
      hasMenu: 'yes',
      acceptsReservations: 'yes',
      servesCuisine: data.facilities?.find((f) => f.label.includes('food'))?.value,
    } as WithContext<NightClub>,
  };

  const schema = schemaByType[data.type || 'localBusiness'];

  if (!schema) {
    return null;
  }

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}
